// Mantenedor de IA sobre TanStack Query. Mismo patrón que `usePerfiles`: la
// cache de la query es la única fuente de verdad y las mutaciones invalidan la
// key en vez de parchear la lista a mano.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchIaCampos,
  fetchIaFunciones,
  guardarContextoDeCampoRequest,
  guardarIaFuncionRequest,
  olvidarContextoDeCampoRequest,
  type IaFuncionInput,
} from "@/lib/mantenedorDeIa";

export const IA_FUNCIONES_KEY = ["ia", "funciones"] as const;
export const IA_CAMPOS_KEY = ["ia", "campos"] as const;

export function useIaFunciones() {
  return useQuery({
    queryKey: IA_FUNCIONES_KEY,
    queryFn: fetchIaFunciones,
  });
}

/**
 * El catálogo de campos con asistencia.
 *
 * `staleTime` alto: describe el PRODUCTO, no la configuración. Cambia cuando
 * se construye una pantalla nueva, no mientras alguien edita un prompt.
 */
export function useIaCampos() {
  return useQuery({
    queryKey: IA_CAMPOS_KEY,
    queryFn: fetchIaCampos,
    staleTime: 30 * 60 * 1000,
  });
}

export function useGuardarIaFuncion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: IaFuncionInput & { id: number }) =>
      guardarIaFuncionRequest(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: IA_FUNCIONES_KEY }),
  });
}

export function useGuardarContextoDeCampo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      funcionId,
      campo,
      instruccion,
    }: {
      funcionId: number;
      campo: string;
      instruccion: string;
    }) => guardarContextoDeCampoRequest(funcionId, campo, instruccion),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: IA_FUNCIONES_KEY }),
  });
}

export function useOlvidarContextoDeCampo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ funcionId, campo }: { funcionId: number; campo: string }) =>
      olvidarContextoDeCampoRequest(funcionId, campo),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: IA_FUNCIONES_KEY }),
  });
}
